import React, { RefObject, memo, useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { runOnJS, useAnimatedProps, useAnimatedReaction } from 'react-native-reanimated';
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useRoute } from '@react-navigation/native';

import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { Handle, MODAL_HANDLE_HEIGHT } from '@/feature/alerts/components/Modal';
import { SHEET_BORDER_RADIUS } from '@/feature/circular-pager/CircularPageSlot';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { ScrollableModalContextProvider } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { CircularPagerImperativeApi } from '@/feature/circular-pager/types';
import { PlayerProfileSheetScreen } from '@/feature/player-profile-sheets/components/PlayerProfileSheetScreen';
import { PlayerProfileTitleWithScrollableModal } from '@/feature/player-profile-sheets/components/PlayerProfileTitle';
import { usePlayerProfileDataStore } from '@/feature/player-profile-sheets/hooks/use-player-profile-data-store';
import { common, withOpacity } from '@/styles/styles';
import BottomSheet, { BottomSheetBackdropProps, useBottomSheetGestureHandlers } from '@gorhom/bottom-sheet';

import { FantasyEventsSheetsParamsList } from '../FantasyEventsSheets';
import { useSinglePlayerProfile } from '../SinglePlayerProfileProvider';
import { getEventCarouselHeight } from './EventsCarouselTabBar';
import { SinglePlayerProfilePickbar } from './SinglePlayerProfilePickbar';

const ONLY_ONE_EVENT_HEIGHT = 4;

export const SinglePlayerProfileModal = memo(
    ({
        onlyOnePage,
        circularPagerRef,
    }: {
        onlyOnePage: boolean;
        circularPagerRef: RefObject<CircularPagerImperativeApi | null>;
    }) => {
        const { height } = useSafeAreaFrame();
        const { playerProfileModalRef, playerProfileAnimatedIndex, isPlayerProfileModalOpen } =
            useSinglePlayerProfile();
        const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();
        const insets = useSafeAreaInsets();
        const [animatedIndexValue, setAnimatedIndexValue] = useState(0);

        const onClosePlayerProfileModal = useCallback(() => {
            isPlayerProfileModalOpen.value = false;
            playerProfileAnimatedIndex.value = -1;
            usePlayerProfileDataStore.getState().actions.setSelectedSinglePlayer(null);
            circularPagerRef.current?.setInteractionsEnabled(true);
        }, [circularPagerRef, isPlayerProfileModalOpen, playerProfileAnimatedIndex]);

        const closeModal = useCallback(() => {
            playerProfileModalRef.current?.close();
        }, [playerProfileModalRef]);
        const { animatedIndex } = useFullScreenPagerTransition();
        useAnimatedReaction(
            () => animatedIndex.value,
            value => {
                if (Number.isInteger(value)) {
                    runOnJS(setAnimatedIndexValue)(value);
                }
            },
            [animatedIndex]
        );

        const topContentHeight = useMemo(() => {
            if (onlyOnePage || animatedIndexValue > 0) {
                return ONLY_ONE_EVENT_HEIGHT;
            }
            return getEventCarouselHeight(params.league);
        }, [animatedIndexValue, onlyOnePage, params.league]);

        return (
            <>
                <ScrollableModalContextProvider>
                    <BottomSheet
                        index={-1}
                        snapPoints={[height - topContentHeight - insets.top - 12, height - insets.top - 16]}
                        enablePanDownToClose
                        enableDynamicSizing={false}
                        animatedIndex={playerProfileAnimatedIndex}
                        ref={playerProfileModalRef}
                        style={styles.bottomSheet}
                        onClose={onClosePlayerProfileModal}
                        onChange={index => {
                            const hasOpened = index !== -1;
                            if (hasOpened) {
                                circularPagerRef.current?.setInteractionsEnabled(false);
                            }
                        }}
                        backgroundStyle={styles.bottomSheetBackground}
                        backdropComponent={CustomBackdrop}
                        handleStyle={styles.hide}
                    >
                        <ModalContent closeModal={closeModal} />
                    </BottomSheet>
                </ScrollableModalContextProvider>
                <SinglePlayerProfilePickbar />
            </>
        );
    }
);

// used to block behind the bottom sheet when the bottom sheet is open
const CustomBackdrop = ({ animatedIndex }: BottomSheetBackdropProps) => {
    const animatedProps = useAnimatedProps(() => {
        const pointerEvents: 'auto' | 'box-none' = animatedIndex.value > -1 ? 'auto' : 'box-none';

        return {
            pointerEvents,
        };
    });

    return <Animated.View animatedProps={animatedProps} style={common.flex} />;
};

const ModalContent = memo(({ closeModal }: { closeModal: () => void }) => {
    const selectedSinglePlayer = usePlayerProfileDataStore(state => state.selectedSinglePlayer);

    const { handlePanGestureHandler } = useBottomSheetGestureHandlers();

    const panGesture = useMemo(() => {
        return Gesture.Pan()
            .shouldCancelWhenOutside(false)
            .runOnJS(false)
            .onStart(handlePanGestureHandler.handleOnStart)
            .onChange(handlePanGestureHandler.handleOnChange)
            .onEnd(handlePanGestureHandler.handleOnEnd)
            .onFinalize(handlePanGestureHandler.handleOnFinalize);
    }, [handlePanGestureHandler]);

    return selectedSinglePlayer ? (
        <>
            <Box style={styles.outerBorderWrapper} />
            <Box style={styles.innerBackgroundOverlay} />

            <Box
                position={'absolute'}
                zIndex={2}
                width={'100%'}
                height={EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT}
                overflow={'hidden'}
            >
                <GestureDetector gesture={panGesture}>
                    <Animated.View>
                        <Handle
                            handleColor={withOpacity(ds.colorValue.background.neutral.inverse, 0.15)}
                            handleContainerStyle={common.backgroundColorTransparent}
                        />
                        <PlayerProfileTitleWithScrollableModal
                            fromSinglePlayerProfile
                            playerInfo={selectedSinglePlayer}
                            onClose={closeModal}
                        />
                    </Animated.View>
                </GestureDetector>
            </Box>
            <PlayerProfileSheetScreen
                playerInfo={selectedSinglePlayer}
                closeSinglePlayerProfile={closeModal}
                isActive={true}
            />
        </>
    ) : null;
});

const INNER_RADIUS = SHEET_BORDER_RADIUS - 1; // slightly smaller to reveal border arc
const BORDER_HEIGHT = SHEET_BORDER_RADIUS;
const styles = StyleSheet.create({
    bottomSheetBackground: {
        backgroundColor: ds.colorValue.background.neutral.base,
        borderCurve: 'continuous',
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
    },
    bottomSheet: {
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
        borderCurve: 'continuous',
        overflow: 'hidden',
    },
    hide: {
        display: 'none',
    },
    outerBorderWrapper: {
        backgroundColor: ds.colorValue.background.transparent.subtle,
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
        height: BORDER_HEIGHT,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
    },
    innerBackgroundOverlay: {
        backgroundColor: ds.colorValue.background.neutral.base,
        borderTopLeftRadius: INNER_RADIUS,
        borderTopRightRadius: INNER_RADIUS,
        height: BORDER_HEIGHT,
        marginTop: 1,
        width: '100%',
        position: 'absolute',
        left: 0,
    },
});
